import { Button } from '@ads/components-react';
import { CheckBadgeIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import type { IndividualPaymentInvoiceViewModel } from '@/shared/api/generated';
import { Skeleton } from '@/shared/ui/atoms';

import { formatContractDate } from '../../../utils/formatContractDate';
import { EMPTY_VALUE } from '../../../utils/formatCurrency';
import { ContractStatusBadge } from '../../atoms/ContractStatusBadge';
import { PlanSectionCard } from '../../molecules/PlanSectionCard';

/** Props de `ParcelInvoicesTable`. */
export interface ParcelInvoicesTableProps {
  readonly invoices: readonly IndividualPaymentInvoiceViewModel[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly onRetry: () => void;
}

/**
 * Grid compartilhado entre cabeçalho e linhas dos comprovantes.
 *
 * São as sete colunas da tela legada, na mesma ordem. Status tem largura fixa
 * porque carrega uma Tag; as demais são proporcionais, e os três campos de RPS e
 * verificação ficam mais estreitos por serem códigos curtos.
 */
const ROW_GRID =
  'grid grid-cols-[minmax(7rem,0.8fr)_minmax(9rem,1.2fr)_minmax(8rem,1fr)_minmax(8rem,1fr)_minmax(6rem,0.7fr)_minmax(5rem,0.6fr)_minmax(7rem,0.8fr)] gap-x-nano';

/** Padding horizontal comum às linhas — sem ele as colunas desalinham. */
const ROW_PADDING_X = 'px-mini';

const CELL = 'truncate ts-paragraph-xxs text-ctx-content-base';

/**
 * Tabela somente leitura dos comprovantes de pagamento de uma parcela.
 *
 * Alimentada por `GET /api/v1/individualpayment/parcels/{parcelId}/invoices`, é a
 * mesma tabela do rodapé da tela legada. Somente leitura de propósito: reemitir,
 * cancelar e enviar comprovante são operações próprias de
 * `individualPaymentInvoicesController`, com efeito imediato — misturá-las ao
 * Salvar do formulário faria uma ação irreversível depender de um botão que o
 * operador aciona pensando em ter alterado datas e valores.
 *
 * Os estados de carregamento, vazio e erro são locais à seção: uma falha aqui não
 * impede editar a parcela.
 *
 * Segue o frame `Upir5` de `design/payment.pen`.
 */
export function ParcelInvoicesTable({
  invoices,
  isLoading,
  isError,
  onRetry,
}: ParcelInvoicesTableProps) {
  const { t } = useTranslation('individualPayment');

  return (
    <PlanSectionCard title={t('parcelEdit.invoices.title')} icon={CheckBadgeIcon}>
      {isError ? (
        <div className="flex flex-col items-center gap-micro py-xs text-center">
          <span className="flex items-center justify-center rounded-pill bg-base-danger-100 p-micro">
            <ExclamationTriangleIcon className="h-xxs w-xxs text-base-danger-700" />
          </span>
          <p className="ts-paragraph-xs text-ctx-content-title">{t('parcelEdit.invoices.error')}</p>
          <Button hierarchy="secondary" size="sm" onClick={onRetry}>
            {t('parcels.table.retry')}
          </Button>
        </div>
      ) : isLoading ? (
        <InvoicesSkeleton />
      ) : invoices.length === 0 ? (
        <p className="py-xs text-center ts-paragraph-xs text-ctx-content-placeholder">
          {t('parcelEdit.invoices.empty')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[52rem]">
            <div
              className={`${ROW_GRID} ${ROW_PADDING_X} items-center border-b-hairline border-ctx-layout-border py-micro`}
            >
              {/* Chaves literais para que `t()` valide em tempo de compilação — uma
                  chave montada em runtime perderia a checagem. */}
              {[
                t('parcelEdit.invoices.columns.status'),
                t('parcelEdit.invoices.columns.reason'),
                t('parcelEdit.invoices.columns.number'),
                t('parcelEdit.invoices.columns.verificationCode'),
                t('parcelEdit.invoices.columns.rpsNumber'),
                t('parcelEdit.invoices.columns.rpsSerie'),
                t('parcelEdit.invoices.columns.generatedAt'),
              ].map(label => (
                <span key={label} className="ts-subtitle-xxs text-ctx-content-title">
                  {label}
                </span>
              ))}
            </div>

            {invoices.map((invoice, index) => (
              <InvoiceRow
                // O ViewModel não tem id próprio: `nfeNumber` identifica a linha na
                // maioria dos casos, e o índice cobre o comprovante ainda sem número.
                key={invoice.nfeNumber ?? `invoice-${index}`}
                invoice={invoice}
              />
            ))}
          </div>
        </div>
      )}
    </PlanSectionCard>
  );
}

function InvoiceRow({ invoice }: { readonly invoice: IndividualPaymentInvoiceViewModel }) {
  return (
    <div
      className={`${ROW_GRID} ${ROW_PADDING_X} items-center border-b-hairline border-ctx-layout-border py-micro last:border-b-none`}
    >
      <span className="flex min-w-none items-center">
        {invoice.statusText ? (
          <ContractStatusBadge status={invoice.statusText} />
        ) : (
          <span className="ts-caption-xxs text-ctx-content-placeholder">{EMPTY_VALUE}</span>
        )}
      </span>
      <span className={CELL}>{invoice.statusReason ?? EMPTY_VALUE}</span>
      <span className={`${CELL} tabular-nums`}>{invoice.nfeNumber ?? EMPTY_VALUE}</span>
      <span className={`${CELL} tabular-nums`}>{invoice.nfeVerificationCode ?? EMPTY_VALUE}</span>
      <span className={`${CELL} tabular-nums`}>{invoice.nfeRpsNumber ?? EMPTY_VALUE}</span>
      <span className={CELL}>{invoice.nfeRpsSerie ?? EMPTY_VALUE}</span>
      <span className={`${CELL} tabular-nums`}>
        {formatContractDate(invoice.nfeCompetenceDate)}
      </span>
    </div>
  );
}

function InvoicesSkeleton() {
  return (
    <div className="flex flex-col">
      {Array.from({ length: 2 }, (_, index) => `invoice-skeleton-${index}`).map(key => (
        <div
          key={key}
          className={`${ROW_GRID} ${ROW_PADDING_X} items-center border-b-hairline border-ctx-layout-border py-micro`}
        >
          <Skeleton className="h-xs w-[4.5rem] rounded-pill" />
          <Skeleton className="h-xs w-[7rem] rounded-sm" />
          <Skeleton className="h-xs w-[5rem] rounded-sm" />
          <Skeleton className="h-xs w-[5rem] rounded-sm" />
          <Skeleton className="h-xs w-[3.5rem] rounded-sm" />
          <Skeleton className="h-xs w-[2.5rem] rounded-sm" />
          <Skeleton className="h-xs w-[5rem] rounded-sm" />
        </div>
      ))}
    </div>
  );
}
